import {
  Box,
  CircularProgress,
  Dialog,
  DialogProps,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useModalStyles, useModalTitleStyles } from './modal.styles';
import CloseIcon from '@mui/icons-material/Close';
import { useTextStyles } from '../../../theme/text';
import clsx from 'clsx';

export interface ModalProps {
  open: boolean;
  title: string;
  divider?: boolean;
  onClose?: () => void;
  loading?: boolean;
  isEmptyWhenLoading?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  /**
   * Scrolls the `DialogContent` between the title and a `DialogActions` footer, which then stays in
   * view however long the content is. Without it, the whole dialog scrolls, footer included.
   */
  pinFooter?: boolean;
  children?: React.ReactNode;
}

export const Modal = ({
  open,
  title,
  divider = false,
  onClose,
  loading = false,
  isEmptyWhenLoading = false,
  maxWidth = 'md',
  pinFooter = false,
  children = null
}: ModalProps) => {
  const classes = useModalStyles();
  const titleClasses = useModalTitleStyles({ divider });
  const textClasses = useTextStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog fullWidth={true} open={open} onClose={onClose} fullScreen={fullScreen} maxWidth={maxWidth}>
      <DialogTitle className={titleClasses.dialogTitle}>
        <Box className={titleClasses.titleContainer}>
          <Typography className={textClasses.h2}>{title}</Typography>
          {onClose ? (
            <IconButton aria-label="close" size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>
      {loading && isEmptyWhenLoading ? (
        <Box className={classes.placeholderContent} />
      ) : (
        <Box className={clsx(classes.modalContent, pinFooter && classes.pinnedFooterContent)}>{children}</Box>
      )}
      {loading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress style={{ color: '#ffffff' }} />
        </Box>
      )}
    </Dialog>
  );
};
